// PancakeDrift — canvas-rendered ambient cutout pancakes floating up through
// the hero. Zero DOM image elements — one <canvas>, all images drawn via
// ctx.drawImage(). When a pancake leaves the screen, its texture is explicitly
// dereferenced so the browser can release the decoded bitmap immediately.
// Memory-flat regardless of how many pancakes cycle through over time.
//
// Source: `config/publicPancakeSample` (PII-free, public-readable).
// Prefers 400px JPEG thumbnails (thumbUrl) over full-res cutout PNGs.

import { useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import { trackDriftClick } from "../../../utils/analytics";

const MAX_DESKTOP = 7;
const MAX_MOBILE  = 3;
const DESKTOP_BP  = 768;

const SIZE_MIN_D = 138, SIZE_MAX_D = 281;
const SIZE_MIN_M = 70,  SIZE_MAX_M = 145;
const DUR_MIN = 26000, DUR_MAX = 44000;
const OPACITY_PEAK = 0.28;

const TIERS = [
  { w: 2, size: 0.62, opa: 0.65, spd: 0.7, wob: 0.6,  z: 1 },
  { w: 5, size: 1.0,  opa: 1.0,  spd: 1.0, wob: 1.0,  z: 2 },
  { w: 3, size: 1.35, opa: 1.25, spd: 1.3, wob: 1.25, z: 3 },
];

function r(a, b) { return a + Math.random() * (b - a); }

function pickTier() {
  const tot = TIERS.reduce((s, t) => s + t.w, 0);
  let v = Math.random() * tot;
  for (const t of TIERS) { v -= t.w; if (v <= 0) return t; }
  return TIERS[2];
}

function pickStartX(existing) {
  if (!existing.length) return r(10, 90);
  let best = 50, bestD = -1;
  for (let i = 0; i < 12; i++) {
    const c = r(5, 95);
    const d = Math.min(...existing.map(x => Math.abs(c - x)));
    if (d > bestD) { bestD = d; best = c; }
  }
  return best;
}

// Lerp through keyframes: 0→12% fade in, 12→85% full, 85→100% fade out
function opacityAt(t, peak) {
  if (t < 0.12) return (t / 0.12) * peak;
  if (t < 0.85) return peak;
  if (t < 1) return peak * 0.7 * (1 - (t - 0.85) / 0.15);
  return 0;
}

// Y: 120vh → -20vh (bottom to top). Lerped from keyframe stops.
function yAt(t, h) {
  const stops = [[0, 1.2], [0.12, 1.2 - 1.2*0.18], [0.5, 1.2 - 1.2*0.6], [0.85, 1.2 - 1.2*1.02], [1, -0.2]];
  for (let i = 1; i < stops.length; i++) {
    if (t <= stops[i][0]) {
      const [t0, y0] = stops[i-1], [t1, y1] = stops[i];
      const p = (t - t0) / (t1 - t0);
      return (y0 + (y1 - y0) * p) * h;
    }
  }
  return -0.2 * h;
}

export default function PancakeDrift() {
  const canvasRef = useRef(null);
  const poolRef = useRef([]);
  const driftersRef = useRef([]);
  const recentRef = useRef([]);
  const rafRef = useRef(null);
  const [pool, setPool] = useState(null);

  // Track which pancakes have published meditations
  const meditationIdsRef = useRef(new Set());

  // Firestore subscriptions
  useEffect(() => {
    const unsub1 = onSnapshot(
      doc(db, "config/publicPancakeSample"),
      s => { if (s.exists()) setPool(s.data()?.items || []); else setPool([]); },
      () => setPool([]),
    );
    const unsub2 = onSnapshot(
      doc(db, "config/publicMeditations"),
      s => {
        if (s.exists()) {
          const items = s.data()?.items || [];
          // Shift meditations share this feed but aren't drift pancakes —
          // keep their ids out of the glow set.
          meditationIdsRef.current = new Set(
            items.filter(m => (m.type || "pancake") !== "shift").map(m => m.id)
          );
        }
      },
      () => {},
    );
    return () => { unsub1(); unsub2(); };
  }, []);

  // Keep poolRef in sync for the render loop (avoids stale closures)
  useEffect(() => { poolRef.current = pool || []; }, [pool]);

  // Rendered positions — updated each frame for hit-testing tap/click
  const hitAreasRef = useRef([]); // [{ id, x, y, radius }]

  // Reduced motion check
  const reducedRef = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = mq.matches;
    const u = () => { reducedRef.current = mq.matches; };
    mq.addEventListener?.("change", u);
    return () => mq.removeEventListener?.("change", u);
  }, []);

  // Weighted pick — same logic as before, operates on poolRef
  function pickNext(excludeIds) {
    const p = poolRef.current;
    if (!p.length) return null;
    const excl = new Set(excludeIds);
    const skip = Math.min(Math.floor(p.length / 2), 12);
    recentRef.current.slice(0, skip).forEach(id => excl.add(id));
    let src = p.filter(x => !excl.has(x.id));
    if (!src.length) src = p.filter(x => !excludeIds.includes(x.id));
    if (!src.length) return null;
    const ws = src.map(x => x.meditationPinned ? 4 : x.publicGallery ? 2 : 1);
    const tot = ws.reduce((a, b) => a + b, 0);
    let v = Math.random() * tot, idx = 0;
    for (; idx < ws.length; idx++) { v -= ws[idx]; if (v <= 0) break; }
    if (idx >= src.length) idx = src.length - 1;
    const picked = src[idx];
    recentRef.current = [picked.id, ...recentRef.current].slice(0, 16);
    return picked;
  }

  // Load an image, return a promise that resolves with the HTMLImageElement.
  // On unmount or recycle, we set img.src="" to hint the browser to free the
  // decoded bitmap — the key memory advantage over DOM <img> elements.
  function loadImage(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  // Spawn a drifter object (used by seed + recycle)
  function makeDrifter(pancake, isDesktop, isSeed) {
    const tier = pickTier();
    const sMin = isDesktop ? SIZE_MIN_D : SIZE_MIN_M;
    const sMax = isDesktop ? SIZE_MAX_D : SIZE_MAX_M;
    const size = r(sMin, sMax) * tier.size;
    const dur  = r(DUR_MIN, DUR_MAX) / tier.spd;
    const existing = driftersRef.current.map(d => d.startX);

    return {
      id: pancake.id,
      src: pancake.thumbUrl || pancake.cutoutUrl,
      img: null, // set after load
      size,
      dur,
      startX: pickStartX(existing),
      driftX: r(-10, 10),
      startRot: r(-14, 14),
      driftRot: r(-18, 18),
      peak: OPACITY_PEAK * r(0.85, 1.15) * tier.opa,
      wobbleAmp: r(0.5, 1.6) * tier.wob,
      wobblePeriod: r(16000, 26000),
      wobblePhase: r(0, Math.PI * 2),
      shadowMul: tier.size,
      z: tier.z,
      // Seed drifters start mid-journey (negative offset) so the screen
      // feels alive immediately. A separate fadeInUntil timestamp applies a
      // real-time opacity ramp so they FADE in over 2s instead of popping.
      born: performance.now() + (isSeed ? -r(0, dur - 2000) : r(0, 1500)),
      fadeInUntil: isSeed ? performance.now() + 2000 + r(0, 1500) : 0,
      dead: false,
    };
  }

  // Main canvas loop
  useEffect(() => {
    if (!pool || pool.length === 0) return;
    if (reducedRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const isDesktop = window.innerWidth >= DESKTOP_BP;
    const max = isDesktop ? MAX_DESKTOP : MAX_MOBILE;

    // Resize canvas to fill container
    function resize() {
      const par = canvas.parentElement;
      if (!par) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = par.clientWidth * dpr;
      canvas.height = par.clientHeight * dpr;
      canvas.style.width = par.clientWidth + "px";
      canvas.style.height = par.clientHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener("resize", resize);

    // Seed initial drifters
    const drifters = driftersRef.current;
    drifters.length = 0;
    const activeIds = [];
    for (let i = 0; i < max; i++) {
      const picked = pickNext(activeIds);
      if (!picked) break;
      const d = makeDrifter(picked, isDesktop, true);
      drifters.push(d);
      activeIds.push(d.id);
      // Load image async — drifter renders once img is ready
      loadImage(d.src).then(img => { d.img = img; });
    }

    // Render loop
    function frame() {
      const now = performance.now();
      const W = canvas.width / (window.devicePixelRatio || 1);
      const H = canvas.height / (window.devicePixelRatio || 1);
      ctx.clearRect(0, 0, W, H);

      // Bottom fade mask — draw a gradient overlay after all pancakes
      // Actually, use globalCompositeOperation to mask. Simpler: just
      // reduce opacity for pancakes near the bottom.

      // Sort by z for proper layering
      drifters.sort((a, b) => a.z - b.z);

      const frameHits = [];
      for (const d of drifters) {
        if (!d.img || d.dead) continue;
        const elapsed = now - d.born;
        if (elapsed < 0) continue; // not born yet (delay)
        const t = elapsed / d.dur; // 0..1 progress
        if (t > 1) {
          d.dead = true;
          continue;
        }

        let alpha = opacityAt(t, d.peak);
        // Seed drifters: real-time fade-in over ~2.5s so they materialize
        // gently instead of popping onto screen mid-journey.
        if (d.fadeInUntil > 0 && now < d.fadeInUntil) {
          alpha *= Math.max(0, Math.min(1, 1 - (d.fadeInUntil - now) / 2500));
        }
        if (alpha < 0.005) continue;

        // Position
        const xBase = (d.startX / 100) * W + (d.driftX / 100) * W * t;
        const wobble = Math.sin((elapsed / d.wobblePeriod) * Math.PI * 2 + d.wobblePhase) * (d.wobbleAmp / 100) * W;
        const x = xBase + wobble;
        const y = yAt(t, H);
        const rot = (d.startRot + d.driftRot * t) * Math.PI / 180;

        // Bottom fade: reduce opacity when y > 80% of H
        const bottomFade = y > H * 0.82 ? Math.max(0, 1 - (y - H * 0.82) / (H * 0.18)) : 1;

        ctx.save();
        ctx.globalAlpha = alpha * bottomFade;
        ctx.translate(x, y);
        ctx.rotate(rot);

        // Drop shadow
        const sh = 8 * d.shadowMul;
        ctx.shadowColor = `rgba(0,0,0,${0.4 * d.shadowMul})`;
        ctx.shadowBlur = sh * 2;
        ctx.shadowOffsetY = sh;

        // Preserve aspect ratio (equivalent to object-fit: contain)
        const iw = d.img.naturalWidth || d.img.width;
        const ih = d.img.naturalHeight || d.img.height;
        const scale = Math.min(d.size / iw, d.size / ih);
        const dw = iw * scale, dh = ih * scale;
        ctx.drawImage(d.img, -dw / 2, -dh / 2, dw, dh);
        ctx.restore();

        // Store hit area for click detection (only if reasonably visible)
        if (alpha * bottomFade > 0.15) {
          frameHits.push({ id: d.id, x, y, radius: Math.max(dw, dh) / 2 });
        }
      }
      hitAreasRef.current = frameHits;

      // Recycle dead drifters
      for (let i = drifters.length - 1; i >= 0; i--) {
        if (!drifters[i].dead) continue;
        const dead = drifters[i];
        // Explicitly release the image reference
        if (dead.img) { dead.img.src = ""; dead.img = null; }

        const ids = drifters.filter(d => !d.dead).map(d => d.id);
        const picked = pickNext(ids);
        if (picked) {
          const nd = makeDrifter(picked, isDesktop, false);
          drifters[i] = nd;
          loadImage(nd.src).then(img => { nd.img = img; });
        } else {
          drifters.splice(i, 1);
        }
      }

      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      // Release all image refs on unmount
      for (const d of drifters) {
        if (d.img) { d.img.src = ""; d.img = null; }
      }
      drifters.length = 0;
    };
  }, [pool]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hit-test a canvas-relative point against rendered pancakes (reverse z-order = top first)
  function hitTest(clientX, clientY) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const hits = hitAreasRef.current;
    for (let i = hits.length - 1; i >= 0; i--) {
      const h = hits[i];
      const dx = x - h.x, dy = y - h.y;
      if (dx * dx + dy * dy <= h.radius * h.radius) return h.id;
    }
    return null;
  }

  function handleCanvasClick(e) {
    const id = hitTest(e.clientX, e.clientY);
    if (id) { trackDriftClick(id); window.location.href = `/gallery/${id}`; }
  }

  function handleCanvasMove(e) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const id = hitTest(e.clientX, e.clientY);
    canvas.style.cursor = id ? "pointer" : "";
  }

  if (!pool || pool.length === 0) return null;

  return (
    <div style={{
      position: "absolute", inset: 0,
      overflow: "hidden", zIndex: 1,
    }}>
      <canvas
        ref={canvasRef}
        onClick={handleCanvasClick}
        onMouseMove={handleCanvasMove}
        style={{
          width: "100%", height: "100%", display: "block",
          WebkitMaskImage: "linear-gradient(0deg, transparent 0%, #000 18%, #000 100%)",
          maskImage: "linear-gradient(0deg, transparent 0%, #000 18%, #000 100%)",
        }}
      />
    </div>
  );
}
